import { IContext } from "../../rule-engine-core/Common/IContext";
import { MappingBuilderPage } from "./MappingBuilderPage";
import { MappingConstants } from "../../rule-engine-core/Mapping/MappingConstants";
import { MappingConfiguration } from "../../rule-engine-core/Mapping/MappingConfiguration";
import { StateManager } from "../../rule-engine-core/Common/StateManager";
import { IDesignPanelConfig } from "../../rule-engine-core/Common/IDesignPanelConfig";
import { ReactRenderHook } from "../../CommonUI/ReactRenderHook";

export class MappingEditContext implements IContext {
    definition: MappingConfiguration;

    mode: MappingBuilderPage = MappingBuilderPage.Designer;
    isDragging: boolean = false;
    mappingBuilderName?: string;
    public onSave?: (data: MappingConfiguration) => void;
    public onCancel?: () => void;
    designConfig?: IDesignPanelConfig | undefined;
    runtimeConfig?: IDesignPanelConfig | undefined;
    clipboardElement: any;
    lastSelectionElement: any;
    lineRefleshObject = {};
    private _collapsedDestinationPaths: string[] = [];
    private _collapsedSourcePaths: string[] = [];
    private _highlightedRules: string[] = [];

    constructor(definition: MappingConfiguration) {
        this.definition = definition;
    }

    private _selectedDestination?: string;

    get selectedDestination() {
        return this._selectedDestination;
    }

    set selectedDestination(sel: string | undefined) {
        this._selectedDestination = sel;
    }

    set hightlightedRules(ruleIds: string[]) {
        if (this.isDragging) return;

        this._highlightedRules = ruleIds;

        StateManager.changed(this);
    }

    set hoveredSource(path: string | undefined) {
        if (this.isDragging) return;

        if (path) {
            let rules = this.definition.getMappingRulesByInputPath(path);

            this._highlightedRules = rules.map((r) => r.id);
        } else {
            this._highlightedRules = [];
        }

        StateManager.changed(this);
    }

    private _hoveredExpression: string | undefined;

    get hoveredExpression(): string | undefined {
        return this._hoveredExpression;
    }

    set hoveredExpression(id: string | undefined) {
        if (this.isDragging) return;

        if (id === this._hoveredExpression) return;

        if (id) {
            let mappingRule = this.definition.find(id);
            if (!mappingRule || (mappingRule && mappingRule.empty)) return;

            this._highlightedRules = [id];
        } else {
            this._highlightedRules = [];
        }

        this._hoveredExpression = id;

        StateManager.changed(this);
    }

    toggleDestinationCollapse(path: string) {
        let idx = this._collapsedDestinationPaths.indexOf(path);
        if (idx < 0) {
            this._collapsedDestinationPaths.push(path);
        } else {
            this._collapsedDestinationPaths.splice(idx, 1);
        }
        StateManager.changed(this);
    }

    isDestinationCollapsed(path: string, forChildren: boolean = true): boolean {
        let pieces = path.split(".");

        if (forChildren) {
            for (let i = 1; i < pieces.length; i++) {
                let anchestorPath = pieces.slice(0, i).join(".");

                if (this._collapsedDestinationPaths.includes(anchestorPath)) return true;
            }
        } else {
            return this._collapsedDestinationPaths.includes(path);
        }

        return false;
    }

    toggleSourceCollapse(path: string) {
        let idx = this._collapsedSourcePaths.indexOf(path);
        if (idx < 0) {
            this._collapsedSourcePaths.push(path);
        } else {
            this._collapsedSourcePaths.splice(idx, 1);
        }

        StateManager.changed(this);
    }

    isSourceCollapsed(path: string, forChildren: boolean = true): boolean {
        let pieces = path.split(".");

        if (forChildren) {
            for (let i = 1; i < pieces.length; i++) {
                let anchestorPath = pieces.slice(0, i).join(".");

                if (this._collapsedSourcePaths.includes(anchestorPath)) return true;
            }
        } else {
            return this._collapsedSourcePaths.includes(path);
        }

        return false;
    }

    isHighlighted(ruleId: string): boolean {
        return this._highlightedRules.includes(ruleId);
    }

    isHighlightedSource(path: string): boolean {
        let rules = this.definition.getMappingRulesByInputPath(path);

        if (this._highlightedRules.length === 0) return false;

        for (let rule of rules) {
            if (this._highlightedRules.includes(rule.id)) {
                return true;
            }
        }

        return false;
    }

    // for ui
    sourceId(path: string): string {
        return `${MappingConstants.MAPPING_PREFIX_SOURCE_ITEM}${path}`;
    }

    sourceConnectorIdForParent(path: string): string {
        return `${MappingConstants.MAPPING_PREFIX_SOURCE_ITEM}${path}_connector_forparent`;
    }

    sourceConnectorIdForChildren(path: string): string {
        return `${MappingConstants.MAPPING_PREFIX_SOURCE_ITEM}${path}_connector_forchildren`;
    }

    sourceDragId(path: string): string {
        return `${this.sourceId(path)}${MappingConstants.DRAGGING_CONNECTOR_SUFFIX}`;
    }

    ruleId(id: string): string {
        return `${MappingConstants.MAPPING_PREFIX_RULE_ITEM}${id}`;
    }

    ruleIdForSource(id: string): string {
        return `${MappingConstants.MAPPING_PREFIX_RULE_ITEM}${id}_connector_forsource`;
    }

    ruleIdForDestination(id: string): string {
        return `${MappingConstants.MAPPING_PREFIX_RULE_ITEM}${id}_connector_fordestination`;
    }

    destinationId(path: string): string {
        return `${MappingConstants.MAPPING_PREFIX_DESTINATION_ITEM}${path}`;
    }

    destinationConnectorIdForParent(path: string): string {
        return `${MappingConstants.MAPPING_PREFIX_SOURCE_ITEM}${path}_connector_forparent`;
    }

    destinationConnectorIdForChildren(path: string): string {
        return `${MappingConstants.MAPPING_PREFIX_SOURCE_ITEM}${path}_connector_forchildren`;
    }

    destinationDragId(id: string): string {
        return `${MappingConstants.MAPPING_PREFIX_RULE_ITEM}${id}${MappingConstants.DRAGGING_CONNECTOR_SUFFIX}`;
    }

    // eslint-disable-next-line
    onCopy(id?: string) {}

    // eslint-disable-next-line
    onCut(id?: string) {}

    // eslint-disable-next-line
    onDelete(id?: string) {}

    // eslint-disable-next-line
    onPaste(id?: string) {}

    delayPosition() {
        ReactRenderHook.registerCallback("position", () => {
            StateManager.changed(this.lineRefleshObject);
        });
    }
}
